// Design tokens v1 — extracted from Pencil iteration 2 (Commitment Tracker + hover state).
// To be refined as Pencil delivers Account detail, AI Thread, Settings, Login screens.
// Last updated: 2026-05-03

export const colors = {
  // Surfaces — three-tier hierarchy: canvas (page bg) → raised (cards) → recessed (empty states)
  canvas: '#F5F1E8',              // Parchment
  raised: '#FAFAF7',              // paper-white (cards float above canvas)
  recessed: '#EFEBE0',            // empty-state inset

  // Text
  textPrimary: '#0B0B0F',         // Ink — body, headings
  textSecondary: '#5F5C52',       // Stone-deep — labels, metadata, captions
  // Stone — PLACEHOLDERS ONLY. It misses the WCAG AA 4.5:1 floor for text a
  // reader has to read on all three surfaces (3.15:1 on canvas, 3.40:1 on
  // raised, 2.98:1 on recessed), so an informational caption or label uses
  // textSecondary (5.62–6.40:1). A placeholder the reader is not meant to
  // read may stay tertiary; a caption that carries real information may not.
  // The bank/close 390px axe checks are what hold this (FE-SP-015).
  textTertiary: '#8A8880',
  textInverse: '#FAFAF7',         // for use on Teal/Ember solid backgrounds

  // Borders
  borderSubtle: '#E5E0D2',        // 1px default on cards, dividers
  borderDefault: '#D4CEBC',       // input borders at rest
  borderHover: '#1F4E4A',         // Teal — confirmed by hover state image

  // Brand & accent palette (use sparingly)
  teal: '#1F4E4A',                // primary brand, single primary action per screen
  ember: '#C76B3A',               // urgency, overdue, danger
  gold: '#D9A91E',                // completion, achievement, recently-done
  sage: '#6B8E7F',                // in-progress, on-track, success

  // Pale tint variants (for tag backgrounds, filter pills, avatar circles)
  tealTint: '#D5E0DD',
  emberTint: '#F5DCC8',
  goldTint: '#F5E8C2',
  sageTint: '#E0E8E2',

  // Accessible ink variants — accent-family values that pass WCAG AA
  // (>= 4.5:1) as body text on their own tint and on canvas, raised, and
  // recessed. The plain accents above stay correct for borders, icons,
  // and solid fills; body text uses the ink variants (FE-A11Y-INK).
  // scripts/check-ink-text.ts and the token tests enforce the floor.
  tealInk: '#1F4E4A',             // unchanged accent value, exposed for symmetry
  emberInk: '#A5461F',            // proved in production by MonthStatusStrip
  goldInk: '#7A5E11',
  sageInk: '#4C665B',

  // Tag pills (confirmed by Pencil's Compliance/ACA pills)
  tagCategoryBg: '#E0E8E2',       // pale Sage — for Compliance, Onboarding, Renewal, etc.
  tagCategoryText: '#1F4E4A',     // Teal text on pale Sage bg
  tagRegulatoryBg: '#F5DCC8',     // pale Ember — for ACA, ERISA, COBRA, HIPAA
  tagRegulatoryText: '#A5461F',   // Ember-ink text on pale Ember bg — the accent fails as text (2.85:1)

  // Avatar
  avatarBg: '#E0E8E2',            // pale Sage circle (confirmed by LH, AE, SM, etc.)
  avatarText: '#5F5C52',          // Stone-deep initials

  // Date colorization
  dateOverdue: '#A5461F',         // Ember-ink text for past-due — the accent fails as text on canvas (3.33:1)
  dateFuture: '#5F5C52',          // Stone-deep for future

  // Active filter pill
  filterPillBg: '#E0E8E2',        // pale Sage
  filterPillText: '#1F4E4A',      // Teal
  filterPillBorder: 'transparent',

  // Status — kanban left-edge stripes + CFD chart bands
  status: {
    backlog: 'transparent',       // no stripe (default state)
    ready_for_work: '#8A8880',    // Stone (workflow-ready, not yet started)
    in_progress: '#6B8E7F',       // Sage
    in_review: '#D9A91E',         // Gold
    done: '#D9A91E',              // Gold (fades after a day per spec)
    cancelled: 'transparent',     // visual de-emphasis instead of stripe
  },

  // Stripe overlays (override status stripe when applicable)
  overdue: '#C76B3A',             // Ember stripe for past-due commitments
  blocked: '#8A8880',             // Stone stripe for blocked

  // Priority indicators
  priorityHigh: '#C76B3A',        // Ember dot for high priority
  // Medium and low priority have no marker (priorityHigh is the only signal)

  // Shadow source (used by shadows.ts; included here for reference)
  shadowInk: 'rgba(11, 11, 15, 0.06)',
} as const;

export type Colors = typeof colors;
